import { TranslocoPipe, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipSelectionChange, MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { CanDirective, CapabilityNames, HeaderService } from 'core';
import { SortColumn, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { debounceTime, forkJoin } from 'rxjs';
import { OrderStatusBadge } from '../../components/order-status-badge/order-status-badge';
import { OrderDraftItem, OrderListItem } from '../../interfaces/order-list.model';
import { OrderProposalService } from '../../services/order-proposal.service';
import { OrdersService, OrdersSort } from '../../services/orders.service';
import { orderNumberLabel, orderTagLabel, subOrdersTooltip } from '../../utils/order.utils';
import { DeleteDraftsDialog } from './delete-drafts-dialog/delete-drafts-dialog';
import { DraftSelectCell } from './draft-select-cell/draft-select-cell';

// Filtri rapidi del backend: tutti gli ordini, in autorizzazione, in blocco, da fatturare o spedire, spediti
export enum OrdersStatus { ALL, IN_APPROVAL, BLOCKED, AWAITING_SHIPPING, SHIPPED }

interface StatusChip {
  status: OrdersStatus;
  icon: string;
  iconVariant: string;
  label: string;
  count: number;
  amount: number | null;
  filtroRapido: string | null;
}

interface OrderItem {
  // Le commesse nate in ERP non hanno l'ordine in App: si aprono dal carrello
  id: string;
  idCarrello: string | null;
  numeroOrdine: string;
  cliente: string;
  importo: number | null;
  provvigione: number | null;
  data: string | null;
  stato: string;
  sottoOrdini: string;
}

type DraftItem = Omit<OrderItem, 'idCarrello' | 'numeroOrdine' | 'provvigione' | 'sottoOrdini'> & {
  nome: string | null;
  isSelected: () => boolean;
  toggle: () => void;
};

// Colonne ordinabili e proprietà del backend
const ORDERS_SORT_MAP: Record<string, string> = {
  numeroOrdine: 'Numero',
  cliente: 'RagioneSociale',
  importo: 'Importo',
  provvigione: 'Provvigione',
  data: 'Data',
};

const DRAFTS_SORT_MAP: Record<string, string> = {
  nome: 'Descrizione',
  cliente: 'RagioneSociale',
  importo: 'Totale',
  data: 'DataInserimento',
};

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-orders-list',
  imports: [TranslocoPipe,
    ReactiveFormsModule, MatChipsModule, MatFormFieldModule, MatButtonModule, MatCheckboxModule,
    MatInputModule, MatButtonToggleModule, MatPaginatorModule, TableView, TranslocoCurrencyPipe, CanDirective,
  ],
  templateUrl: './orders-list.html',
  styleUrl: './orders-list.scss',
})
export class OrdersList implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ordersService = inject(OrdersService);
  private readonly proposalService = inject(OrderProposalService);

  readonly capabilityNames = CapabilityNames;

  readonly statusChips: StatusChip[] = [
    { status: OrdersStatus.ALL, icon: 'fa-cart-shopping', iconVariant: 'regular', get label() { return translate<string>('orders.ui.tuttiGliOrdini'); }, count: 0, amount: null, filtroRapido: null },
    { status: OrdersStatus.IN_APPROVAL, icon: 'fa-clock', iconVariant: 'regular', get label() { return translate<string>('orders.ui.inAutorizzazione'); }, count: 0, amount: null, filtroRapido: 'InAttesaAutorizzazione' },
    { status: OrdersStatus.BLOCKED, icon: 'fa-ban', iconVariant: 'regular', get label() { return translate<string>('orders.ui.ordiniInBlocco'); }, count: 0, amount: null, filtroRapido: 'InBlocco' },
    { status: OrdersStatus.AWAITING_SHIPPING, icon: 'fa-truck-fast', iconVariant: 'regular', get label() { return translate<string>('orders.ui.fattSpedizione'); }, count: 0, amount: null, filtroRapido: 'InAttesaSpedizione' },
    { status: OrdersStatus.SHIPPED, icon: 'fa-box-circle-check', iconVariant: 'regular', get label() { return translate<string>('orders.ui.spedito'); }, count: 0, amount: null, filtroRapido: 'Spedito' },
  ];

  selectedStatus = OrdersStatus.ALL;

  readonly viewOptions = [
    { value: 'orders', get label() { return translate<string>('orders.ui.ordini'); } },
    { value: 'drafts', get label() { return translate<string>('orders.ui.bozze2'); } },
  ];

  readonly columnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'numeroOrdine',
      get label() { return translate<string>('orders.ui.numeroOrdine'); },
      type: TypeOfColumn.TEXT,
      labelOnOneLine: true,
      sortable: true,
    },
    {
      columnNameDef: 'cliente',
      get label() { return translate<string>('orders.ui.cliente'); },
      type: TypeOfColumn.TEXT,
      sortable: true,
    },
    {
      columnNameDef: 'importo',
      get label() { return translate<string>('orders.ui.importo'); },
      type: TypeOfColumn.CURRENCY,
      sortable: true,
    },
    {
      columnNameDef: 'provvigione',
      get label() { return translate<string>('orders.ui.provvigione'); },
      type: TypeOfColumn.CURRENCY,
      sortable: true,
    },
    {
      columnNameDef: 'data',
      get label() { return translate<string>('orders.ui.data'); },
      type: TypeOfColumn.DATE,
      dateFormat: 'dd/MM/yyyy',
      sortable: true,
    },
    {
      // Il tag è composto dal client: il backend non lo ordina
      columnNameDef: 'stato',
      get label() { return translate<string>('orders.ui.stato'); },
      type: TypeOfColumn.COMPONENT,
      component: OrderStatusBadge,
      sortable: false,
    },
  ];

  orders: OrderItem[] = [];

  // Le bozze non hanno numero d'ordine né provvigione: hanno il nome e la selezione per eliminarle
  readonly draftsColumnsInfo: TableColumnInfo[] = [
    { columnNameDef: 'selection', label: '', type: TypeOfColumn.COMPONENT, component: DraftSelectCell },
    { columnNameDef: 'nome', get label() { return translate<string>('orders.ui.nomeBozza'); }, type: TypeOfColumn.TEXT, labelOnOneLine: true, sortable: true },
    ...this.columnsInfo.filter(
      column => column.columnNameDef !== 'numeroOrdine' && column.columnNameDef !== 'provvigione',
    ).map(column => column.columnNameDef === 'stato'
      ? { ...column, get label() { return translate<string>('orders.ui.preventivo'); }, type: TypeOfColumn.TEXT, component: undefined }
      : column),
  ];

  drafts: DraftItem[] = [];
  readonly selectedDraftIds = new Set<string>();
  showOnlyQuotes = false;
  draftsCount = 0;

  selectedView = 'orders';
  readonly searchText = new FormControl('', { nonNullable: true });
  currentSortColumns: SortColumn[] = [];

  totalItems = 0;
  pageSize = 10;
  pageIndex = 0; // zero-based per mat-paginator
  private loadRequestId = 0;

  onOrderClick(order: OrderItem): void {
    void this.router.navigate(['/order-detail'], {
      queryParams: order.idCarrello ? { idCarrello: order.idCarrello } : { id: order.id },
    });
  }

  // La bozza si riapre sempre dal backend, che ripete i controlli; al ritorno la lista si rilegge
  onDraftClick(draft: DraftItem): void {
    this.proposalService.clear();
    void this.router.navigate(['/new-order-proposal'], { queryParams: { id: draft.id } });
  }

  // Le proposte nuove partono vuote; al ritorno si torna sulle bozze
  newOrder(): void {
    void this.router.navigate(['/new-order-proposal']);
  }

  onViewChange(value: string): void {
    this.selectedView = value;
    this.pageIndex = 0;
    this.currentSortColumns = [];
    this.selectedDraftIds.clear();
    // La vista resta nell'indirizzo: tornando dalla proposta si ritrova
    void this.router.navigate([], { queryParams: { view: value }, replaceUrl: true });
    this.updatePagedRows();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedRows();
  }

  onSortChange(sortColumns: SortColumn[]): void {
    this.currentSortColumns = sortColumns;
    this.pageIndex = 0;
    this.updatePagedRows();
  }

  onChipSelectedChange(event: MatChipSelectionChange, status: OrdersStatus): void {
    if (!event.selected) {
      // La pulsantiera deve avere sempre un filtro attivo
      if (this.selectedStatus === status) {
        event.source.select();
      }
      return;
    }

    if (this.selectedStatus === status) return;

    this.selectedStatus = status;
    this.pageIndex = 0;
    this.updatePagedRows();
  }

  onShowOnlyQuotesChange(value: boolean): void {
    this.showOnlyQuotes = value;
    this.pageIndex = 0;
    this.updatePagedRows();
  }

  clearDraftsSelection(): void {
    this.selectedDraftIds.clear();
  }

  // Una DELETE per bozza; quelle con il preventivo in autorizzazione le rifiuta il backend
  deleteSelectedDrafts(): void {
    const ids = Array.from(this.selectedDraftIds);
    this.dialog.open(DeleteDraftsDialog, { width: '400px', data: { count: ids.length } })
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) {
          this.selectedDraftIds.clear();
          return;
        }

        forkJoin(ids.map(id => this.ordersService.deleteDraft(id))).subscribe({
          next: () => {
            this.selectedDraftIds.clear();
            this.updatePagedRows();
          },
          error: () => {
            this.selectedDraftIds.clear();
            this.updatePagedRows();
          },
        });
      });
  }

  private updatePagedRows(): void {
    if (this.selectedView === 'orders') {
      this.loadOrders();
    } else {
      this.loadDrafts();
    }
  }

  private loadOrders(): void {
    const requestId = ++this.loadRequestId;
    const filtroRapido = this.statusChips.find(chip => chip.status === this.selectedStatus)?.filtroRapido ?? null;

    this.ordersService.getOrders(this.pageIndex + 1, this.pageSize, this.searchText.value, filtroRapido, this.sort(ORDERS_SORT_MAP))
      .subscribe(result => {
        if (requestId !== this.loadRequestId) return;

        this.orders = result.items.map(item => this.toOrderItem(item));
        this.totalItems = result.totalItems;
      });
  }

  // I contatori seguono la ricerca, non il filtro rapido scelto
  private loadQuickFilters(): void {
    this.ordersService.getQuickFilters(this.searchText.value).subscribe(quickFilters => {
      const values: Record<OrdersStatus, [number, number | null]> = {
        [OrdersStatus.ALL]: [quickFilters.tutti, null],
        [OrdersStatus.IN_APPROVAL]: [quickFilters.inAttesaAutorizzazione, quickFilters.valoreInAttesaAutorizzazione],
        [OrdersStatus.BLOCKED]: [quickFilters.inBlocco, quickFilters.valoreInBlocco],
        [OrdersStatus.AWAITING_SHIPPING]: [quickFilters.inAttesaSpedizione, quickFilters.valoreInAttesaSpedizione],
        [OrdersStatus.SHIPPED]: [quickFilters.spediti, quickFilters.valoreSpediti],
      };

      for (const chip of this.statusChips) {
        [chip.count, chip.amount] = values[chip.status];
      }
    });
  }

  private loadDrafts(): void {
    const requestId = ++this.loadRequestId;

    this.ordersService.getDrafts(this.pageIndex + 1, this.pageSize, this.searchText.value, this.showOnlyQuotes, this.sort(DRAFTS_SORT_MAP))
      .subscribe(result => {
        if (requestId !== this.loadRequestId) return;

        this.drafts = result.items.map(item => this.toDraftItem(item));
        this.totalItems = result.totalItems;
        this.draftsCount = result.totalItems;
      });
  }

  private sort(sortMap: Record<string, string>): OrdersSort[] {
    return this.currentSortColumns
      .filter(sort => sortMap[sort.column])
      .map(sort => ({ propertyName: sortMap[sort.column], orderType: sort.direction === 'desc' ? 'Desc' : 'Asc' }));
  }

  private toOrderItem(item: OrderListItem): OrderItem {
    return {
      id: item.idOrdine ?? item.idCarrello ?? '',
      idCarrello: item.idOrdine ? null : item.idCarrello,
      numeroOrdine: orderNumberLabel(item),
      cliente: item.ragioneSociale ?? '-',
      importo: item.importo,
      provvigione: item.provvigione,
      data: item.data,
      stato: orderTagLabel(item),
      sottoOrdini: subOrdersTooltip(item),
    };
  }

  private toDraftItem(item: OrderDraftItem): DraftItem {
    return {
      id: item.id,
      nome: item.descrizione,
      cliente: item.ragioneSociale ?? '-',
      importo: item.totale,
      data: item.dataInserimento,
      stato: item.preventivoStato === 'Generato' || item.preventivoStato === 'InAutorizzazione' ? 'Preventivo' : '',
      isSelected: () => this.selectedDraftIds.has(item.id),
      toggle: () => this.selectedDraftIds.has(item.id)
        ? this.selectedDraftIds.delete(item.id)
        : this.selectedDraftIds.add(item.id),
    };
  }

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.ordini');

    this.selectedView = this.route.snapshot.queryParamMap.get('view') === 'drafts' ? 'drafts' : 'orders';

    this.searchText.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadQuickFilters();
        this.updatePagedRows();
      });

    this.loadQuickFilters();
    this.updatePagedRows();
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
  }
}
